import { Expand, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { LoadingStatus } from '../spinner';

import './visual-learning.css';

/** Reads the semantic tokens at render time so the diagram follows the theme without hex in code. */
function themeVariables(): Record<string, string> {
  const css = getComputedStyle(document.documentElement);
  const read = (name: string) => css.getPropertyValue(name).trim();
  const fg = read('--fg');
  const line = read('--line-strong');
  const surface = read('--surface');
  const raised = read('--surface-raised');
  return {
    fontFamily: css.getPropertyValue('--font-sans').trim() || 'sans-serif',
    fontSize: '13px',
    primaryColor: surface,
    primaryTextColor: fg,
    primaryBorderColor: line,
    secondaryColor: raised,
    tertiaryColor: raised,
    lineColor: fg,
    textColor: fg,
    edgeLabelBackground: raised,
    clusterBkg: raised,
    clusterBorder: line,
    actorBkg: surface,
    actorBorder: line,
    actorTextColor: fg,
    signalColor: fg,
    signalTextColor: fg,
    labelBoxBkgColor: surface,
    labelBoxBorderColor: line,
    noteBkgColor: raised,
    noteBorderColor: line,
    noteTextColor: fg,
  };
}

function currentTheme(): string {
  return document.documentElement.getAttribute('data-theme') ?? 'light';
}

/**
 * Renders a Mermaid source block to SVG. Mermaid is loaded on first use so lessons without
 * diagrams never pay for it. Re-renders when the theme attribute changes.
 */
export function MermaidDiagram({
  source,
  onStatusChange,
}: {
  source: string;
  onStatusChange?: (status: 'pending' | 'rendered' | 'failed') => void;
}) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState(currentTheme);
  const [expanded, setExpanded] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const title = /^\s*accTitle:\s*(.+)$/m.exec(source)?.[1] ?? 'Mechanism map';
  const description = /^\s*accDescr:\s*(.+)$/m.exec(source)?.[1];

  useEffect(() => {
    onStatusChange?.(error ? 'failed' : svg ? 'rendered' : 'pending');
  }, [error, svg, onStatusChange]);

  useEffect(() => {
    if (expanded) dialog.current?.showModal();
    else if (dialog.current?.open) dialog.current.close();
  }, [expanded]);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(currentTheme()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          themeVariables: themeVariables(),
        });
        const result = await mermaid.render(`mermaid-${id}-${theme}`, source);
        if (!cancelled) {
          setSvg(result.svg);
          setError(null);
        }
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Diagram failed to render');
          setExpanded(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, source, theme]);

  if (error) {
    return (
      <div
        className="border-line text-fg-muted my-4 rounded-md border p-4 text-xs"
        role="note"
        data-diagram-status="failed"
        data-diagram-error={error.slice(0, 400)}
        aria-label={title}
      >
        <p>The diagram could not load.</p>
        <p>{description ?? 'Follow the mechanism explanation and worked example below.'}</p>
      </div>
    );
  }
  if (!svg) {
    return (
      <div
        className="bg-sidebar border-line my-4 rounded-lg border p-6"
        data-diagram-status="pending"
      >
        <LoadingStatus label="Rendering diagram…" className="text-xs" />
      </div>
    );
  }
  return (
    <figure className="mermaid-map" data-diagram-status="rendered">
      <figcaption>
        <span>{title}</span>
        <button
          type="button"
          aria-label={`Expand diagram: ${title}`}
          onClick={() => setExpanded(true)}
        >
          <Expand size={14} aria-hidden="true" /> Expand
        </button>
      </figcaption>
      {!expanded ? (
        // Mermaid output comes from repository-authored source and uses strict security mode.
        <div className="mermaid-map-preview" dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <div className="mermaid-map-placeholder">Diagram open in expanded view</div>
      )}
      <dialog
        ref={dialog}
        className="mermaid-map-dialog"
        aria-label={title}
        onClose={() => setExpanded(false)}
      >
        <header>
          <span>{title}</span>
          <button
            type="button"
            aria-label="Close expanded diagram"
            onClick={() => setExpanded(false)}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        {expanded ? (
          <div className="mermaid-map-expanded" dangerouslySetInnerHTML={{ __html: svg }} />
        ) : null}
      </dialog>
    </figure>
  );
}
